import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';

import { SearchState } from '../services/search-state';
import { ContactKind } from '../types';

const useStyles = makeStyles((theme: any) => ({
  container: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { marginTop: theme.spacing(3) },
  },
  message: {
    textAlign: 'center',
    overflowWrap: 'anywhere',
    color: theme.palette.text.shadow,
  },
  messageError: { color: theme.palette.error.main },
  option: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
    },
  },
  // Stacked options split by a top rule on phones, a left rule from sm up.
  divider: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    [theme.breakpoints.up('sm')]: {
      borderTop: 'none',
      borderLeft: `1px solid ${theme.palette.grey[400]}`,
    },
  },
  // FontAwesome sizes from font-size (svg height: 1em).
  icon: {
    fontSize: theme.spacing(7),
    color: theme.palette.background.component,
    [theme.breakpoints.up('sm')]: { fontSize: theme.spacing(10) },
  },
  button: {
    textTransform: 'none',
    marginTop: theme.spacing(2),
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.secondary.main,
    },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  buttonIcon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
  },
}));

type OptionProps = {
  icon: IconProp;
  buttonText: string;
  onClick: () => void;
};

const Option: FC<OptionProps> = ({ icon, buttonText, onClick }) => {
  const classes = useStyles();

  return (
    <Grid container direction="column" alignItems="center" className={classes.option}>
      <FontAwesomeIcon icon={icon} className={classes.icon} aria-hidden />
      <Button
        disableElevation
        color="secondary"
        variant="outlined"
        className={classes.button}
        onClick={onClick}
      >
        <FontAwesomeIcon icon="plus" className={classes.buttonIcon} />
        {buttonText}
      </Button>
    </Grid>
  );
};

type EmptyContainerProps = {
  t: TFunction;
  searchState: Extract<SearchState, 'idle' | 'empty' | 'error'>;
  searchText: string | undefined;
  handleUserCreateModal: (state: boolean, kind: ContactKind) => void;
};

const EmptyContainer: FC<EmptyContainerProps> = ({
  t,
  searchState,
  searchText,
  handleUserCreateModal,
}) => {
  const classes = useStyles();

  const message = {
    idle: t('parties:type-as-3-characters'),
    empty: t('contact:no-results', { text: searchText?.trim() }),
    error: t('contact:search-error'),
  }[searchState];

  return (
    <Grid container direction="row" className={classes.container}>
      <Grid item xs={12}>
        <Typography
          variant="body2"
          role={searchState === 'error' ? 'alert' : 'status'}
          className={searchState === 'error' ? `${classes.message} ${classes.messageError}` : classes.message}
        >
          {message}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Option
          icon="file-signature"
          buttonText={t('contact:add-organization')}
          onClick={() => handleUserCreateModal(true, 'Organization')}
        />
      </Grid>

      <Grid item xs={12} sm={6} className={classes.divider}>
        <Option
          icon="user"
          buttonText={t('contact:add-person')}
          onClick={() => handleUserCreateModal(true, 'Person')}
        />
      </Grid>
    </Grid>
  );
};

export default EmptyContainer;
